import { getAxiosInstance } from './axiosInstance';
import { getScimConfig } from '../utils/envUtils';
import type { EntityEntitlementsResponse, EntityEntitlementForSchemaResponse, EntityEntitlementForTargetResponse } from '../types/EntityEntitlements';
import { createEntityRef } from '../types/entity/EntityRef';
import { extractErrorMessage } from '../utils/apiUtils';
import { debugService } from './DebugService';
import ImpersonationService from './ImpersonationService';
import { tier2SponsoredImperator } from '../__fixtures__';

/** Minimal tier1 payload for Storybook so EntityTypesProvider and stories render without auth/API. */
function getStorybookTier1Stub(): EntityEntitlementsResponse {
    const entityIds = ['Group', 'Set', 'Sponsored', 'Employee', 'SchemaEntity', 'Building'];
    return {
        Requestor: { Id: 'storybook', EntityType: 'Sponsored', DisplayName: 'Storybook' },
        IsAdministrator: true,
        EntitySets: { Person: ['Employee', 'Sponsored', 'Contractor'] },
        EntityEntitlements: entityIds.map((Id) => ({
            Id,
            DisplayName: Id,
            Disjointed: false,
            DeleteEntity: true,
            CreateEntity: true,
            ModifyEntity: true,
            ReadEntity: true,
            EntitySets: Id === 'Employee' || Id === 'Sponsored' ? ['Person'] : [],
            PolicyRules: []
        }))
    };
}

/**
 * Cache key for "who we were asking": current user vs on-behalf-of.
 * When on-behalf-of is enabled and a UPN is set, we always use that UPN so we never
 * read the true user's cache (isAdmin, etc.) when the user intends to view as someone else.
 */
export function getRequestorCacheKey(): string {
    if (ImpersonationService.getOnBehalfOfEnabled()) {
        const upn = ImpersonationService.getOnBehalfOfUpn().trim();
        if (upn) return upn;
    }
    const header = ImpersonationService.getOnBehalfOfHeader();
    return header['x-on-behalf-of'] ?? 'current';
}

export type EntityEntitlementsPayload = EntityEntitlementsResponse | null;

class EntitlementsService {
    /** Tier1: keyed by requestor (current vs on-behalf-of UPN). */
    private tier1Cache: Map<string, EntityEntitlementsResponse> = new Map();
    private tier1Loading = false;
    private tier1LoadingKey: string | null = null;
    private tier1Error: string | null = null;
    private listeners: Set<() => void> = new Set();
    private api = getAxiosInstance(getScimConfig().apiUrl);

    /** Tier2 (per-entity): outer key = requestor, inner key = schemaEntityId (lowercase). */
    private tier2Cache: Map<string, Map<string, EntityEntitlementForSchemaResponse>> = new Map();
    /** In-flight tier2: composite key "requestorKey:entityId" so we dedupe per requestor. */
    private tier2FetchInFlight: Map<string, Promise<EntityEntitlementForSchemaResponse | null>> = new Map();

    /**
     * Fetch entity entitlements from mgmt/Entitlements/entity and cache the result.
     * Cache is keyed by requestor (current user vs x-on-behalf-of UPN).
     * @param forceRefresh - when true, start a new request even if one is in progress (e.g. after on-behalf-of toggle).
     */
    async fetchEntityEntitlements(forceRefresh = false): Promise<EntityEntitlementsPayload | null> {
        if (process.env.STORYBOOK === 'true') {
            const stub = getStorybookTier1Stub();
            const requestorKey = getRequestorCacheKey();
            this.tier1Cache.set(requestorKey, stub);
            this.tier1Error = null;
            this.notifyListeners();
            return stub;
        }

        const requestorKey = getRequestorCacheKey();
        const cached = this.tier1Cache.get(requestorKey);
        if (this.tier1Loading && this.tier1LoadingKey === requestorKey && !forceRefresh) {
            return cached ?? null;
        }
        this.tier1Loading = true;
        this.tier1LoadingKey = requestorKey;
        this.tier1Error = null;
        try {
            const params: Record<string, string> = {};
            if (debugService.isEnabled()) {
                params.Debug = 'true';
            }
            // Send x-on-behalf-of for the requestor we're caching so the API returns that user's entitlements (isAdmin, etc.)
            const config: { params: Record<string, string>; meta?: { onBehalfOfUpn?: string } } = { params };
            if (requestorKey !== 'current') {
                config.meta = { onBehalfOfUpn: requestorKey };
            }
            const response = await this.api.get<EntityEntitlementsResponse>('/mgmt/Entitlements/entity', config);
            const data = response.data;
            const hadCache = this.tier1Cache.has(requestorKey);
            const parsed = data && typeof data === 'object' && Array.isArray((data as any).EntityEntitlements)
                ? (data as EntityEntitlementsResponse)
                : null;
            if (parsed) this.tier1Cache.set(requestorKey, parsed);
            else this.tier1Cache.delete(requestorKey);
            // Clear tier2 cache only for this requestor when we refreshed (e.g. on-behalf-of toggle)
            if (hadCache) this.clearPerEntityCache(requestorKey);
            this.notifyListeners();
            return parsed;
        } catch (err) {
            this.tier1Error = err instanceof Error ? err.message : 'Unknown error';
            this.tier1Cache.delete(requestorKey);
            this.notifyListeners();
            throw err;
        } finally {
            this.tier1Loading = false;
            this.tier1LoadingKey = null;
            // Ensure subscribers see loading=false even on success.
            this.notifyListeners();
        }
    }

    /** Get cached tier1 for the current requestor (from getRequestorCacheKey()). */
    getCachedEntityEntitlements(): EntityEntitlementsResponse | null {
        return this.tier1Cache.get(getRequestorCacheKey()) ?? null;
    }

    /**
     * Fetch entitlements for a single schema entity (tier2, table-level).
     * Cached per requestor + schemaEntityId so on-behalf-of and current user don't share data.
     * Uses the shared axios instance (x-on-behalf-of is sent when set in the footer).
     * Dedupes in-flight per requestor+entity.
     */
    /** Normalize schema entity id for API/cache: use ref displayName so "$.Building" and "Building" both become "building". */
    private static schemaEntityIdToKey(schemaEntityId: string): string {
        const s = (schemaEntityId ?? '').toString().trim();
        return s ? createEntityRef(s).displayName.trim().toLowerCase() : '';
    }

    async fetchEntityEntitlementForSchemaEntity(schemaEntityId: string): Promise<EntityEntitlementForSchemaResponse | null> {
        if (!schemaEntityId?.trim()) return null;
        const entityKey = EntitlementsService.schemaEntityIdToKey(schemaEntityId);
        if (process.env.STORYBOOK === 'true') {
            if (entityKey === 'sponsored') return Promise.resolve(tier2SponsoredImperator);
            return Promise.resolve(null);
        }
        const requestorKey = getRequestorCacheKey();
        const compositeKey = `${requestorKey}:${entityKey}`;
        const existing = this.tier2FetchInFlight.get(compositeKey);
        if (existing) return existing;
        const cached = this.tier2Cache.get(requestorKey)?.get(entityKey);
        if (cached != null) return Promise.resolve(cached);
        const params: Record<string, string> = {};
        if (debugService.isEnabled()) params.Debug = 'true';
        const promise = this.api
            .get<EntityEntitlementForSchemaResponse>(
                `/mgmt/Entitlements/entity/${encodeURIComponent(entityKey)}`,
                { params: Object.keys(params).length ? params : undefined }
            )
            .then((response) => {
                const data = response?.data ?? null;
                if (data) {
                    let perRequestor = this.tier2Cache.get(requestorKey);
                    if (!perRequestor) {
                        perRequestor = new Map();
                        this.tier2Cache.set(requestorKey, perRequestor);
                    }
                    perRequestor.set(entityKey, data);
                }
                return data;
            })
            .catch(() => null)
            .finally(() => {
                this.tier2FetchInFlight.delete(compositeKey);
            });
        this.tier2FetchInFlight.set(compositeKey, promise);
        return promise;
    }

    /** Get cached tier2 (per-entity) for current requestor. Optional requestorKey for callers that need a specific key. */
    getCachedEntityEntitlementForSchemaEntity(schemaEntityId: string, requestorKey?: string): EntityEntitlementForSchemaResponse | null {
        const key = requestorKey ?? getRequestorCacheKey();
        const entityKey = EntitlementsService.schemaEntityIdToKey(schemaEntityId ?? '');
        return this.tier2Cache.get(key)?.get(entityKey) ?? null;
    }

    /**
     * Tier3: fetch entitlement for a specific entity instance (detail view).
     * GET /mgmt/Entitlements/entity/{schemaEntityId}/{targetId}
     * Not cached; callers (e.g. detail dialog) cache in component state if needed.
     */
    async fetchEntityEntitlementForTarget(
        schemaEntityId: string,
        targetId: string
    ): Promise<EntityEntitlementForTargetResponse | null> {
        if (!schemaEntityId?.trim() || !targetId?.trim()) return null;
        const schemaKey = EntitlementsService.schemaEntityIdToKey(schemaEntityId);
        const targetKey = targetId.trim();
        const params: Record<string, string> = {};
        if (debugService.isEnabled()) params.Debug = 'true';
        try {
            const response = await this.api.get<EntityEntitlementForTargetResponse>(
                `/mgmt/Entitlements/entity/${encodeURIComponent(schemaKey)}/${encodeURIComponent(targetKey)}`,
                { params: Object.keys(params).length ? params : undefined }
            );
            return response?.data ?? null;
        } catch (e: unknown) {
            const status = (e as { response?: { status?: number } })?.response?.status;
            if (status === 403 || status === 404) {
                const message = extractErrorMessage(e) || 'You do not have permission to view this entity.';
                throw new Error(message);
            }
            return null;
        }
    }

    /** Clear tier2 cache. When requestorKey is omitted, clears for current requestor (e.g. after tier1 refresh). */
    clearPerEntityCache(requestorKey?: string): void {
        const key = requestorKey ?? getRequestorCacheKey();
        this.tier2Cache.delete(key);
        for (const composite of this.tier2FetchInFlight.keys()) {
            if (composite.startsWith(key + ':')) this.tier2FetchInFlight.delete(composite);
        }
    }

    isLoading(): boolean {
        return this.tier1Loading;
    }

    getError(): string | null {
        return this.tier1Error;
    }

    subscribe(listener: () => void): () => void {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    private notifyListeners(): void {
        this.listeners.forEach(l => l());
    }
}

const entitlementsService = new EntitlementsService();
export default entitlementsService;
